import { useState, useEffect, useCallback, useMemo } from 'react';
import { client } from '../../api/client';
import { useI18n } from '../../i18n';

// Time slots for the day (8 AM to 8 PM)
const TIME_SLOTS = [
  '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', 
  '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'
];

// Day names for headers
const DAY_NAMES = {
  sr: ['Ned', 'Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
};

const MONTH_NAMES = {
  sr: ['Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun', 'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
};

interface AvailabilityCalendarProps {
  defaultExpanded?: boolean;
}

// Helper to format date as YYYY-MM-DD
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Generate next 30 days from today
function generateNext30Days(): Date[] {
  const days: Date[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  for (let i = 0; i < 30; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    days.push(date);
  }
  return days;
}

export function AvailabilityCalendar({ defaultExpanded = false }: AvailabilityCalendarProps) {
  const { language } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [bookedSlots, setBookedSlots] = useState<string[]>([]); // Format: "YYYY-MM-DD HH:mm"
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  // Current week view (0 = this week, 1 = next week, etc.)
  const [weekOffset, setWeekOffset] = useState(0);
  
  // Selected slots - key format: "YYYY-MM-DD-HH:mm"
  const [selectedSlots, setSelectedSlots] = useState<Set<string>>(new Set());

  // Generate the 30 days
  const allDays = useMemo(() => generateNext30Days(), []);
  
  // Group days into weeks (7 days each)
  const weeks = useMemo(() => {
    const result: Date[][] = [];
    for (let i = 0; i < allDays.length; i += 7) {
      result.push(allDays.slice(i, Math.min(i + 7, allDays.length)));
    }
    return result;
  }, [allDays]);
  
  // Current visible week
  const currentWeek = weeks[weekOffset] || [];

  const loadSlots = useCallback(async () => {
    try {
      const res = await client.api.fetch('/api/scheduling/availability');
      const data = await res.json();
      if (data.success) {
        setBookedSlots(data.booked_slots || []);
        
        // Load saved availability slots
        const selected = new Set<string>();
        if (data.date_slots && Array.isArray(data.date_slots)) {
          data.date_slots.forEach((slot: { date: string; time: string }) => {
            selected.add(`${slot.date}-${slot.time}`);
          });
        }
        setSelectedSlots(selected);
      }
    } catch (err) {
      console.error('Failed to load availability:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  const toggleSlot = (date: Date, time: string) => {
    const dateStr = formatDate(date);
    const key = `${dateStr}-${time}`;
    const newSelected = new Set(selectedSlots);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedSlots(newSelected);
  };

  const toggleDay = (date: Date) => {
    const dateStr = formatDate(date);
    const daySlots = TIME_SLOTS.map(time => `${dateStr}-${time}`);
    const allSelected = daySlots.every(slot => selectedSlots.has(slot));
    
    const newSelected = new Set(selectedSlots);
    if (allSelected) {
      daySlots.forEach(slot => newSelected.delete(slot));
    } else {
      daySlots.forEach(slot => newSelected.add(slot));
    }
    setSelectedSlots(newSelected);
  };

  const saveAvailability = async () => {
    setSaving(true);
    setMessage(null);

    try {
      // Convert selected slots to array of {date, time} objects
      const dateSlots = Array.from(selectedSlots).map(key => {
        const [year, month, day, time] = key.split('-');
        return {
          date: `${year}-${month}-${day}`,
          time: time
        };
      });

      const res = await client.api.fetch('/api/scheduling/availability', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `date_slots=${encodeURIComponent(JSON.stringify(dateSlots))}`,
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('Save availability failed:', res.status, errorText);
        throw new Error(errorText || `HTTP ${res.status}`);
      }

      const data = await res.json();
      if (data.success) {
        setMessage({
          type: 'success',
          text: language === 'sr' ? 'Dostupnost je sačuvana!' : 'Availability saved!',
        });
        setTimeout(() => setMessage(null), 3000);
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (err) {
      console.error('Error saving availability:', err);
      setMessage({
        type: 'error',
        text: language === 'sr' ? 'Greška pri čuvanju' : 'Error saving',
      });
    } finally {
      setSaving(false);
    }
  };

  const clearAll = () => {
    setSelectedSlots(new Set());
  };

  const selectWorkHoursForWeek = () => {
    const workSlots = new Set(selectedSlots);
    // For current visible week, select Mon-Fri 9:00-17:00
    currentWeek.forEach(date => {
      const dayOfWeek = date.getDay();
      // Monday (1) to Friday (5)
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        const dateStr = formatDate(date);
        ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00'].forEach(time => {
          workSlots.add(`${dateStr}-${time}`);
        });
      }
    });
    setSelectedSlots(workSlots);
  };

  const isSlotBooked = (date: Date, time: string): boolean => {
    const dateStr = formatDate(date);
    return bookedSlots.some(slot => {
      // slot format might be "YYYY-MM-DD HH:mm:ss" or similar
      return slot.startsWith(`${dateStr} ${time}`) || slot.startsWith(`${dateStr}T${time}`);
    });
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
        <div className="animate-pulse flex items-center gap-4">
          <div className="w-6 h-6 bg-slate-200 dark:bg-slate-700 rounded"></div>
          <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-48"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-6 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-slate-900 dark:text-white">
              {language === 'sr' ? 'Kalendar dostupnosti (30 dana)' : 'Availability Calendar (30 days)'}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {language === 'sr' 
                ? 'Podesite termine za sledeći mesec' 
                : 'Set available times for the next month'}
            </p>
          </div>
        </div>
        <svg 
          className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-6 pb-6 border-t border-slate-200 dark:border-slate-700">
          {/* Message */}
          {message && (
            <div className={`mt-4 p-3 rounded-lg ${
              message.type === 'success' 
                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400' 
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
            }`}>
              {message.text}
            </div>
          )}

          {/* Week Navigation */}
          <div className="flex items-center justify-between mt-4 mb-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setWeekOffset(Math.max(0, weekOffset - 1))}
                disabled={weekOffset === 0}
                className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300 min-w-[140px] text-center">
                {language === 'sr' ? 'Nedelja' : 'Week'} {weekOffset + 1} / {weeks.length}
              </span>
              <button
                onClick={() => setWeekOffset(Math.min(weeks.length - 1, weekOffset + 1))}
                disabled={weekOffset >= weeks.length - 1}
                className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            
            {/* Quick Actions */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={selectWorkHoursForWeek}
                className="text-sm px-3 py-1.5 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                {language === 'sr' ? 'Radno vreme ove nedelje' : 'Work hours this week'}
              </button>
              <button
                onClick={clearAll}
                className="text-sm px-3 py-1.5 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                {language === 'sr' ? 'Obriši sve' : 'Clear all'}
              </button>
            </div>
          </div>

          {/* Month indicator */}
          {currentWeek.length > 0 && (
            <div className="mb-3 text-center">
              <span className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                {MONTH_NAMES[language][currentWeek[0].getMonth()]} {currentWeek[0].getFullYear()}
                {currentWeek[currentWeek.length - 1].getMonth() !== currentWeek[0].getMonth() && (
                  <> - {MONTH_NAMES[language][currentWeek[currentWeek.length - 1].getMonth()]} {currentWeek[currentWeek.length - 1].getFullYear()}</>
                )}
              </span>
            </div>
          )}

          {/* Calendar Grid - Date Based */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr>
                  <th className="py-2 px-1 text-left text-xs font-medium text-slate-500 dark:text-slate-400 w-16">
                    {language === 'sr' ? 'Vreme' : 'Time'}
                  </th>
                  {currentWeek.map(date => {
                    const dayIndex = date.getDay();
                    const isToday = formatDate(date) === formatDate(new Date());
                    return (
                      <th key={formatDate(date)} className="py-2 px-1 text-center">
                        <button
                          onClick={() => toggleDay(date)}
                          className={`flex flex-col items-center w-full px-2 py-1 rounded-lg transition-colors ${
                            isToday 
                              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' 
                              : 'hover:bg-slate-100 dark:hover:bg-slate-700'
                          }`}
                        >
                          <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                            {DAY_NAMES[language][dayIndex]}
                          </span>
                          <span className={`text-lg font-bold ${
                            isToday 
                              ? 'text-amber-700 dark:text-amber-400' 
                              : 'text-slate-800 dark:text-slate-200'
                          }`}>
                            {date.getDate()}
                          </span>
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {TIME_SLOTS.map(time => (
                  <tr key={time}>
                    <td className="py-1 px-1 text-xs text-slate-500 dark:text-slate-400 font-medium">
                      {time}
                    </td>
                    {currentWeek.map(date => {
                      const dateStr = formatDate(date);
                      const key = `${dateStr}-${time}`;
                      const isSelected = selectedSlots.has(key);
                      const isBooked = isSlotBooked(date, time);
                      const isPast = date < new Date() && formatDate(date) !== formatDate(new Date());
                      
                      return (
                        <td key={key} className="py-1 px-1">
                          <button
                            onClick={() => !isBooked && !isPast && toggleSlot(date, time)}
                            disabled={isBooked || isPast}
                            className={`w-full h-9 rounded-lg transition-all duration-150 ${
                              isPast
                                ? 'bg-slate-50 dark:bg-slate-800 cursor-not-allowed opacity-50'
                                : isBooked
                                  ? 'bg-red-100 dark:bg-red-900/30 cursor-not-allowed border-2 border-red-300 dark:border-red-700'
                                  : isSelected
                                    ? 'bg-emerald-500 hover:bg-emerald-600 shadow-sm'
                                    : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 border border-slate-200 dark:border-slate-600'
                            }`}
                            title={
                              isPast
                                ? (language === 'sr' ? 'Prošlo' : 'Past')
                                : isBooked 
                                  ? (language === 'sr' ? 'Zauzeto' : 'Booked') 
                                  : isSelected 
                                    ? (language === 'sr' ? 'Dostupno' : 'Available') 
                                    : (language === 'sr' ? 'Klikni da označiš kao dostupno' : 'Click to mark available')
                            }
                          >
                            {isBooked && (
                              <span className="text-sm">📅</span>
                            )}
                            {isSelected && !isBooked && (
                              <span className="text-white text-xs font-medium">✓</span>
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-slate-500 dark:text-slate-400">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-emerald-500 rounded flex items-center justify-center text-white text-[10px]">✓</div>
              <span>{language === 'sr' ? 'Dostupno' : 'Available'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-slate-100 dark:bg-slate-700 rounded border border-slate-200 dark:border-slate-600"></div>
              <span>{language === 'sr' ? 'Nedostupno' : 'Unavailable'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-100 dark:bg-red-900/30 rounded border-2 border-red-300 dark:border-red-700 flex items-center justify-center text-[10px]">📅</div>
              <span>{language === 'sr' ? 'Zakazano' : 'Booked'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-amber-100 dark:bg-amber-900/30 rounded"></div>
              <span>{language === 'sr' ? 'Danas' : 'Today'}</span>
            </div>
          </div>

          {/* Selected Count */}
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-400">
              {language === 'sr' 
                ? `Označili ste ${selectedSlots.size} dostupnih termina u sledećih 30 dana.`
                : `You have marked ${selectedSlots.size} available slots in the next 30 days.`}
            </p>
          </div>

          {/* Upcoming Appointments Info */}
          {bookedSlots.length > 0 && (
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
              <p className="text-sm text-amber-700 dark:text-amber-400">
                <strong>{language === 'sr' ? 'Napomena:' : 'Note:'}</strong>{' '}
                {language === 'sr' 
                  ? `Imate ${bookedSlots.length} zakazanih termina. Polja sa 📅 označavaju već zauzete termine.`
                  : `You have ${bookedSlots.length} booked appointments. Cells with 📅 indicate already booked slots.`}
              </p>
            </div>
          )}

          {/* Save Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={saveAvailability}
              disabled={saving}
              className="px-6 py-2.5 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {saving && (
                <div className="w-4 h-4 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
              )}
              {language === 'sr' ? 'Sačuvaj dostupnost' : 'Save availability'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
